import store from '../store'
import { PERSIST_STORE_NAME } from 'constants/app.constant'
import deepParseJson from 'utils/deepParseJson'

/**
 * Decode JWT payload without verifying signature (client-side convenience only).
 */
function decodeJwtPayload(token) {
    if (!token || typeof token !== 'string') return null
    try {
        const parts = token.split('.')
        if (parts.length < 2) return null
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
        return JSON.parse(json)
    } catch {
        return null
    }
}

function readPersistedAuth() {
    try {
        const raw = localStorage.getItem(PERSIST_STORE_NAME)
        if (!raw) return {}
        const persistData = deepParseJson(raw)
        return persistData?.auth || {}
    } catch {
        return {}
    }
}

function pickCompanyId(...candidates) {
    for (const value of candidates) {
        if (value === null || value === undefined || value === '') continue
        const asString = String(value).trim()
        if (!asString || asString === 'null' || asString === 'undefined') continue
        if (!/^\d+$/.test(asString)) continue
        return asString
    }
    return ''
}

/**
 * Resolve current auth context for API headers.
 * Prefers live Redux state, then persisted storage, then JWT claims.
 */
export function getAuthContext() {
    let session = {}
    let user = {}

    try {
        const state = store.getState()?.auth
        session = state?.session || {}
        user = state?.user || {}
    } catch {
        // store may be unavailable in rare edge cases
    }

    const persisted = readPersistedAuth()
    if (!session?.token) {
        session = persisted.session || session
    }
    if (!user?.companyId && !user?.organizationId) {
        user = { ...persisted.user, ...user }
    }

    const token = session?.token || ''
    const jwt = decodeJwtPayload(token)

    const companyId = pickCompanyId(
        user?.companyId,
        user?.organizationId,
        user?.CompanyId,
        user?.OrganizationId,
        jwt?.CompanyId,
        jwt?.companyId,
        jwt?.orgId
    )

    const userId =
        user?.id ||
        user?.userId ||
        user?.Id ||
        jwt?.nameid ||
        jwt?.sub ||
        jwt?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        ''

    return {
        token,
        companyId,
        userId: userId ? String(userId) : '',
        user,
    }
}

export function getCompanyIdForHeaders(customOrgId = null) {
    if (customOrgId !== null && customOrgId !== undefined && customOrgId !== '') {
        return pickCompanyId(customOrgId) || String(customOrgId).trim()
    }
    return getAuthContext().companyId
}

export default getAuthContext
